import { type FC, type JSX } from 'react';

import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';

import { Link, type LinkProps } from '@craft/ui/components/buttons';

import { getSupportArticleUrl, SupportArticleType } from '@/src/config';

export interface SupportArticleLinkProps extends Omit<LinkProps<'a'>, 'endIcon' | 'href'> {
  readonly articleType: 'base' | SupportArticleType;
  readonly endIcon?: JSX.Element | null;
  readonly query?: Record<string, string>;
}

const LocalSupportArticleLink: FC<SupportArticleLinkProps> = ({
  articleType,
  children,
  endIcon,
  query,
  ...props
}) => {
  const defaultEndIcon =
    props.variant === 'inline-link' || endIcon === null ? undefined : OpenInNewOutlined;
  return (
    <Link
      shouldOpenInNewTab
      {...props}
      endIcon={endIcon ?? defaultEndIcon}
      href={getSupportArticleUrl(articleType, { query })}
    >
      {children ?? 'Learn More'}
    </Link>
  );
};

type SupportArticleLinkPartials = Record<
  SupportArticleType,
  FC<Omit<SupportArticleLinkProps, 'articleType'>>
>;

export const SupportArticleLink = Object.assign(LocalSupportArticleLink, {
  ...Object.values(SupportArticleType).reduce(
    (acc: SupportArticleLinkPartials, articleType) => ({
      ...acc,
      [articleType]: (props: Omit<SupportArticleLinkProps, 'articleType'>) => (
        <LocalSupportArticleLink articleType={articleType} {...props} />
      ),
    }),
    {} as SupportArticleLinkPartials,
  ),
  Base: (props: Omit<SupportArticleLinkProps, 'articleType'>) => (
    <LocalSupportArticleLink articleType='base' {...props} />
  ),
});
